import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyProfile, updateMyProfile, publishMyProfile } from '../../lib/api';
import { toast } from 'sonner';
import { WizardLayout, WizardQuestion, WizardSection } from '../../components/onboarding/WizardLayout';
import { ChipMultiSelect } from '../../components/onboarding/ChipMultiSelect';
import { TransitModeSelect, RadiusSlider } from '../../components/onboarding/TravelSelect';
import { RateInput } from '../../components/onboarding/RateInput';
import { ExperienceSelect } from '../../components/onboarding/ExperienceSelect';
import { ProfilePreviewCard } from '../../components/onboarding/ProfilePreviewCard';
import { CheckCircle, ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import debounce from 'lodash.debounce';

const TOTAL_STEPS = 8; // 7 steps + publish screen

const DEFAULT_INSTRUMENTS = [
  'Vocals', 'Guitar', 'Bass', 'Drums', 'Keys', 'Sax', 
  'Trumpet', 'Violin', 'DJ', 'Percussion', 'Producer'
];

const DEFAULT_EVENT_TYPES = [
  'Clubs', 'Festivals', 'Corporate', 'Weddings', 
  'Private events', 'Theatre', 'Touring', 'Studio sessions',
  'Bar/Cafe', 'Live Broadcasts'
];

const DEFAULT_LANGUAGES = [
  'Dutch', 'English', 'German', 'French', 'Spanish',
  'Portuguese', 'Italian', 'Turkish', 'Arabic', 'Polish'
];

export default function MusicianOnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [profile, setProfile] = useState({
    displayName: '',
    instruments: [],
    eventTypes: [],
    languages: [],
    primaryTransitMode: [],
    travelRadiusKm: 50,
    rateAmount: null,
    onboardingRateType: 'flat',
    musicianExperienceLevel: null,
  });

  // Load profile on mount
  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const response = await getMyProfile();
      const p = response.data;
      
      // Check if already published
      if (p.profileStatus === 'published') {
        navigate('/musicians/me');
        return;
      }
      
      setProfile({
        ...profile,
        ...p,
        displayName: p.displayName || '',
        instruments: p.instruments || [],
        eventTypes: p.eventTypes || [],
        languages: p.languages || [],
        primaryTransitMode: Array.isArray(p.primaryTransitMode) ? p.primaryTransitMode : p.primaryTransitMode ? [p.primaryTransitMode] : [],
        travelRadiusKm: p.travelRadiusKm || 50,
        onboardingRateType: p.onboardingRateType || 'flat',
      });
      
      // Resume at last step — but no saved name means step 1, so drafts from
      // before the stage-name step still get asked for it
      if (p.displayName && p.lastOnboardingStep && p.lastOnboardingStep <= TOTAL_STEPS) {
        setCurrentStep(p.lastOnboardingStep);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        navigate('/login');
        return;
      }
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Debounced autosave
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce(async (data) => {
      try {
        await updateMyProfile(data);
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error('Session expired. Please sign in again.');
          navigate('/login');
          return;
        }
        console.error('Autosave failed:', error);
      }
    }, 600),
    []
  );

  const updateField = (field, value) => {
    const updated = { ...profile, [field]: value };
    setProfile(updated);
    // Trim the name here too, or this autosave can land after Continue's trimmed save
    debouncedSave({ [field]: field === 'displayName' ? value.trim() : value });
  };

  const saveAndContinue = async () => {
    setSaving(true);
    try {
      const nextStep = currentStep + 1;
      await updateMyProfile({
        ...getStepData(),
        lastOnboardingStep: nextStep,
      });
      setCurrentStep(nextStep);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        navigate('/login');
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getStepData = () => {
    switch (currentStep) {
      case 1:
        return { displayName: profile.displayName.trim() };
      case 2:
        return { instruments: profile.instruments };
      case 3:
        return { eventTypes: profile.eventTypes };
      case 4:
        return { languages: profile.languages };
      case 5:
        return { 
          primaryTransitMode: profile.primaryTransitMode,
          travelRadiusKm: profile.travelRadiusKm,
        };
      case 6:
        return {
          rateAmount: profile.rateAmount,
          onboardingRateType: profile.onboardingRateType,
        };
      case 7:
        return { musicianExperienceLevel: profile.musicianExperienceLevel };
      default:
        return {};
    }
  };

  const canContinue = () => {
    switch (currentStep) {
      case 1:
        return profile.displayName.trim().length > 0;
      case 2:
        return profile.instruments.length > 0;
      case 3:
        return profile.eventTypes.length > 0;
      case 4:
        return profile.languages.length > 0;
      case 5:
        const transit = profile.primaryTransitMode;
        return Array.isArray(transit) ? transit.length > 0 : !!transit;
      case 6:
        return profile.rateAmount && profile.onboardingRateType;
      case 7:
        return profile.musicianExperienceLevel;
      case 8:
        return true;
      default:
        return false;
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await publishMyProfile();
      toast.success('Profile published! You are now visible in the directory.');
      navigate('/musicians/me');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        navigate('/login');
        return;
      }
      const errors = error.response?.data?.detail?.errors;
      if (errors) {
        toast.error(errors.join(', '));
      } else {
        toast.error(error.response?.data?.detail || 'Failed to publish profile');
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--s8-amber)]"></div>
      </div>
    );
  }

  // Publish screen (step 8)
  if (currentStep === 8) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] p-6 pb-24">
        <div className="w-full max-w-lg mx-auto">
          {/* Back button */}
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-white/40 hover:text-white/60 text-sm mb-6 transition-colors"
            data-testid="publish-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto bg-[color-mix(in_srgb,var(--s8-amber)_20%,transparent)] rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-[var(--s8-amber)]" />
            </div>
            <h1 className="text-2xl font-semibold text-white mb-2">Ready to Publish?</h1>
            <p className="text-white/50 text-sm">
              Review your profile below. Once published, you'll appear in the musician directory.
            </p>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-br from-[#1e1915] to-[#171411] border border-white/10 rounded-xl p-5 text-left mb-6 space-y-4">
            {/* Stage Name */}
            <div className="flex justify-between items-start">
              <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Stage Name</span>
              <span className="text-white text-sm text-right">{profile.displayName}</span>
            </div>

            {/* Instruments */}
            <div>
              <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Instruments</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {profile.instruments.map(i => (
                  <span key={i} className="px-2.5 py-1 text-xs rounded-md bg-[color-mix(in_srgb,var(--s8-amber)_15%,transparent)] text-[var(--s8-amber)] border border-[color-mix(in_srgb,var(--s8-amber)_30%,transparent)]">{i}</span>
                ))}
              </div>
            </div>

            {/* Event Types */}
            <div>
              <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Events</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {profile.eventTypes.map(e => (
                  <span key={e} className="px-2.5 py-1 text-xs rounded-md bg-white/[0.06] text-white/70 border border-white/10">{e}</span>
                ))}
              </div>
            </div>

            {/* Languages */}
            {profile.languages?.length > 0 && (
              <div className="flex justify-between items-start">
                <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Languages</span>
                <span className="text-white text-sm text-right">{profile.languages.join(', ')}</span>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-white/[0.06]" />

            {/* Travel */}
            <div className="flex justify-between">
              <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Travel</span>
              <span className="text-white text-sm">{profile.travelRadiusKm} km radius</span>
            </div>

            {/* Rate */}
            <div className="flex justify-between">
              <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Rate</span>
              <span className="text-[var(--s8-amber)] text-sm font-semibold">€{profile.rateAmount}</span>
            </div>

            {/* Experience */}
            {profile.musicianExperienceLevel && (
              <div className="flex justify-between">
                <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Experience</span>
                <span className="text-white text-sm capitalize">{profile.musicianExperienceLevel.toLowerCase().replace('_', ' ')}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={handlePublish}
              disabled={publishing}
              className="w-full h-14 bg-[var(--s8-amber)] hover:bg-[var(--s8-amber)] text-black font-bold uppercase tracking-wide"
              data-testid="publish-profile-btn"
            >
              {publishing ? 'Publishing...' : 'Publish Profile'}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => navigate('/musicians/me')}
              className="w-full h-12 border-white/10 text-white/70 hover:text-white hover:bg-white/5"
            >
              Add optional details first
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            
            <button
              onClick={() => navigate('/musicians')}
              className="w-full text-white/40 text-sm hover:text-white/60 transition-colors"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <WizardLayout
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS - 1}
      onBack={handleBack}
      onContinue={saveAndContinue}
      canContinue={canContinue()}
      isLoading={saving}
      previewPanel={<ProfilePreviewCard profile={profile} profileType="musician" />}
    >
      {/* Step 1: Stage Name */}
      {currentStep === 1 && (
        <>
          <WizardQuestion
            title="What's Your Stage Name?"
            subtitle="The name venues will book you under. Perform under your own name? Use that."
          />
          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-white/70 text-sm">Stage Name *</Label>
            <Input
              id="displayName"
              value={profile.displayName}
              onChange={(e) => updateField('displayName', e.target.value)}
              placeholder="e.g., DJ Lotte or Lotte de Vries"
              maxLength={120}
              autoFocus
              className="h-12 bg-[#151515] border-white/[0.08] text-white"
              data-testid="stage-name-input"
            />
          </div>
        </>
      )}

      {/* Step 2: Instruments */}
      {currentStep === 2 && (
        <>
          <WizardQuestion
            title="Tell us about your talent"
            subtitle="Select the instruments you play"
          />
          <ChipMultiSelect
            options={DEFAULT_INSTRUMENTS}
            selected={profile.instruments}
            onChange={(val) => updateField('instruments', val)}
            placeholder="Search or add instrument..."
            maxItems={10}
          />
        </>
      )}

      {/* Step 3: Event Types */}
      {currentStep === 3 && (
        <>
          <WizardQuestion
            title="Where do you usually perform?"
            subtitle="Select the types of events you work"
          />
          <ChipMultiSelect
            options={DEFAULT_EVENT_TYPES}
            selected={profile.eventTypes}
            onChange={(val) => updateField('eventTypes', val)}
            placeholder="Search or add event type..."
            maxItems={8}
          />
        </>
      )}

      {/* Step 4: Languages */}
      {currentStep === 4 && (
        <>
          <WizardQuestion
            title="What languages do you speak?"
            subtitle="Helps venues match you to the right crowd"
          />
          <ChipMultiSelect
            options={DEFAULT_LANGUAGES}
            selected={profile.languages}
            onChange={(val) => updateField('languages', val)}
            placeholder="Search or add language..."
            maxItems={5}
          />
        </>
      )}

      {/* Step 5: Travel */}
      {currentStep === 5 && (
        <>
          <WizardQuestion
            title="How do you travel?"
            subtitle="Select your primary transport and travel radius"
          />
          <div className="space-y-8">
            <TransitModeSelect
              value={profile.primaryTransitMode}
              onChange={(val) => updateField('primaryTransitMode', val)}
            />
            <div className="pt-4 border-t border-white/[0.06]">
              <p className="text-xs text-white/40 uppercase tracking-wider text-center mb-4">
                Travel Radius
              </p>
              <RadiusSlider
                value={profile.travelRadiusKm}
                onChange={(val) => updateField('travelRadiusKm', val)}
              />
            </div>
          </div>
        </>
      )}

      {/* Step 6: Rate */}
      {currentStep === 6 && (
        <>
          <WizardQuestion
            title="What is your rate structure?"
            subtitle="Set your base rate and pricing model"
          />
          <RateInput
            amount={profile.rateAmount}
            rateType={profile.onboardingRateType}
            onAmountChange={(val) => updateField('rateAmount', val)}
            onRateTypeChange={(val) => updateField('onboardingRateType', val)}
            profileType="musician"
          />
        </>
      )}

      {/* Step 7: Experience */}
      {currentStep === 7 && (
        <>
          <WizardQuestion
            title="How experienced are you?"
            subtitle="This helps venues find the right fit"
          />
          <ExperienceSelect
            value={profile.musicianExperienceLevel}
            onChange={(val) => updateField('musicianExperienceLevel', val)}
            profileType="musician"
          />
        </>
      )}
    </WizardLayout>
  );
}
